import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./AdminPanel.css";
import { API_BASE_URL, UPLOAD_BASE_URL } from "../../api";

const API_BASE = API_BASE_URL.replace("/api", "/api/admin");

// Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const [isPreview, setIsPreview] = useState(false);
  const textareaRef = React.useRef(null);

  const insertFormatting = (before, after = before) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || "text";
    const newText =
      value.substring(0, start) +
      before +
      selectedText +
      after +
      value.substring(end);

    onChange(newText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos =
        start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const convertMarkdownToHTML = (markdown) => {
    if (!markdown) return "";
    let html = markdown;

    // Code blocks (before inline code)
    html = html.replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>");

    // Headers (must be at start of line)
    html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
    html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
    html = html.replace(/^# (.+)$/gm, "<h1>$1</h1>");

    // Bold and Italic (more precise)
    html = html.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

    // Inline code
    html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

    // Links
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    // Images
    html = html.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 8px; margin: 20px 0;" />'
    );

    // Lists
    html = html.replace(/^\* (.+)$/gm, "<li>$1</li>");
    html = html.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

    // Line breaks and paragraphs
    const lines = html.split("\n");
    let result = "";

    for (let line of lines) {
      const trimmed = line.trim();

      if (trimmed === "") {
        result += "<br/>";
      } else if (
        trimmed.startsWith("<h") ||
        trimmed.startsWith("<pre") ||
        trimmed.startsWith("<ul") ||
        trimmed.startsWith("<li")
      ) {
        result += line + "\n";
      } else if (!line.match(/<\/(h\d|pre|code|ul|li)>/)) {
        result += `<p>${line}</p>\n`;
      } else {
        result += line + "\n";
      }
    }

    return result;
  };

  return (
    <div className="rich-text-editor">
      <div className="editor-toolbar d-flex flex-wrap gap-2 p-3 bg-darker border-bottom border-light-purple">
        <button
          type="button"
          className="toolbar-btn"
          onClick={(e) => {
            e.preventDefault();
            insertFormatting("# ", "");
          }}
          title="Heading 1"
        >
          H1
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={(e) => {
            e.preventDefault();
            insertFormatting("## ", "");
          }}
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={(e) => {
            e.preventDefault();
            insertFormatting("### ", "");
          }}
          title="Heading 3"
        >
          H3
        </button>
        <span className="toolbar-separator">|</span>
        <button
          type="button"
          className="toolbar-btn"
          onClick={(e) => {
            e.preventDefault();
            insertFormatting("**", "**");
          }}
          title="Bold"
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={(e) => {
            e.preventDefault();
            insertFormatting("*", "*");
          }}
          title="Italic"
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={(e) => {
            e.preventDefault();
            insertFormatting("`", "`");
          }}
          title="Code"
        >
          {"</>"}
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={(e) => {
            e.preventDefault();
            insertFormatting("[", "](url)");
          }}
          title="Link"
        >
          🔗
        </button>
        <div className="flex-grow-1"></div>
        <button
          type="button"
          className={`toolbar-btn ${isPreview ? "btn-purple-active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            setIsPreview(!isPreview);
          }}
          title={isPreview ? "Edit Mode" : "Preview Mode"}
        >
          {isPreview ? "✍️ Edit" : "👁️ Preview"}
        </button>
      </div>

      {isPreview ? (
        <div
          className="editor-preview p-4 bg-dark-purple text-white"
          style={{ minHeight: "400px", maxHeight: "600px", overflowY: "auto" }}
          dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(value) }}
        />
      ) : (
        <Form.Control
          ref={textareaRef}
          as="textarea"
          rows={15}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            placeholder ||
            "Start writing your content here...\n\nTips:\n- Use # for headings\n- Use **text** for bold\n- Use *text* for italic\n- Use `code` for inline code\n- Use [link](url) for links"
          }
          className="editor-textarea"
          style={{
            minHeight: "400px",
            maxHeight: "600px",
            fontFamily: "'Courier New', monospace",
            fontSize: "1rem",
            padding: "20px",
            resize: "vertical",
            background: "rgba(10, 5, 20, 0.9)",
            border: "none",
            color: "#fff",
            outline: "none",
          }}
        />
      )}
    </div>
  );
};

function BlogForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem("admin-token");

  const [blog, setBlog] = useState({
    title: { en: "", uz: "" },
    content: { en: "", uz: "" },
    excerpt: { en: "", uz: "" },
    category: { en: "", uz: "" },
    slug: "",
    published: false,
    coverImage: "",
    images: [],
    tags: [],
  });

  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingMultiple, setUploadingMultiple] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBlog = async () => {
    try {
      const res = await axios.get(`${API_BASE}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const found = res.data.blogs.find((b) => b._id === id);
      if (found) setBlog(found);
    } catch (err) {
      console.error(err);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);
    try {
      setUploading(true);
      const res = await axios.post(`${API_BASE}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.data.success) {
        setBlog({
          ...blog,
          coverImage: `${UPLOAD_BASE_URL}${res.data.url}`,
        });
      }
    } catch (err) {
      alert("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleImagesUpload = async (files) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append("images", file);
    });

    try {
      setUploadingMultiple(true);
      const res = await axios.post(`${API_BASE}/upload-multiple`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        const imageUrls = res.data.files.map(
          (f) => `${UPLOAD_BASE_URL}${f.url}`
        );
        setBlog({
          ...blog,
          images: [...(blog.images || []), ...imageUrls],
        });
        alert(`${res.data.count} rasm yuklandi!`);
      }
    } catch (err) {
      alert("Rasmlar yuklashda xatolik: " + err.message);
    } finally {
      setUploadingMultiple(false);
    }
  };

  const removeImage = (index) => {
    setBlog({
      ...blog,
      images: blog.images.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/blog`, blog, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Blog saved successfully!");
      navigate("/admin");
    } catch (err) {
      alert("Error saving blog: " + err.message);
    }
  };

  const addTag = () => {
    if (tagInput.trim()) {
      setBlog({
        ...blog,
        tags: [...(blog.tags || []), tagInput.trim()],
      });
      setTagInput("");
    }
  };

  const removeTag = (index) => {
    setBlog({
      ...blog,
      tags: blog.tags.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="min-vh-100 bg-darker text-white py-5">
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="purple fw-bold">
            {id ? "Edit Blog" : "New Blog Post"}
          </h2>
          <Button variant="outline-light" onClick={() => navigate("/admin")}>
            ← Back to Admin
          </Button>
        </div>

        <Card className="bg-dark-purple border-light-purple shadow-lg">
          <Card.Body className="p-5">
            <Form onSubmit={handleSubmit}>
              <Row className="gy-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Title (English)
                    </Form.Label>
                    <Form.Control
                      value={blog.title.en}
                      onChange={(e) =>
                        setBlog({
                          ...blog,
                          title: { ...blog.title, en: e.target.value },
                        })
                      }
                      className="admin-input-premium"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Title (Uzbek)
                    </Form.Label>
                    <Form.Control
                      value={blog.title.uz}
                      onChange={(e) =>
                        setBlog({
                          ...blog,
                          title: { ...blog.title, uz: e.target.value },
                        })
                      }
                      className="admin-input-premium"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Category (English)
                    </Form.Label>
                    <Form.Control
                      value={blog.category?.en || ""}
                      onChange={(e) =>
                        setBlog({
                          ...blog,
                          category: { ...blog.category, en: e.target.value },
                        })
                      }
                      className="admin-input-premium"
                      placeholder="e.g., Technology, Tutorial, News"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Category (Uzbek)
                    </Form.Label>
                    <Form.Control
                      value={blog.category?.uz || ""}
                      onChange={(e) =>
                        setBlog({
                          ...blog,
                          category: { ...blog.category, uz: e.target.value },
                        })
                      }
                      className="admin-input-premium"
                      placeholder="masalan: Texnologiya, Darslik, Yangiliklar"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Slug (URL)
                    </Form.Label>
                    <Form.Control
                      value={blog.slug}
                      onChange={(e) =>
                        setBlog({
                          ...blog,
                          slug: e.target.value
                            .toLowerCase()
                            .replace(/\s+/g, "-"),
                        })
                      }
                      className="admin-input-premium"
                      placeholder="my-blog-post"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Cover Image
                    </Form.Label>
                    <Form.Control
                      type="file"
                      onChange={(e) => handleImageUpload(e.target.files[0])}
                      className="admin-input-premium"
                      accept="image/*"
                    />
                    {uploading && (
                      <small className="text-info">Uploading...</small>
                    )}
                    {blog.coverImage && (
                      <img
                        src={blog.coverImage}
                        alt="Preview"
                        className="mt-3 rounded shadow"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "200px",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Additional Images (Gallery)
                      <small className="text-muted ms-2">
                        (Maqola uchun qo'shimcha rasmlar - max 30ta)
                      </small>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      multiple
                      onChange={(e) =>
                        handleMultipleImagesUpload(e.target.files)
                      }
                      className="admin-input-premium"
                      accept="image/*"
                    />
                    {uploadingMultiple && (
                      <small className="text-info">
                        Rasmlar yuklanmoqda...
                      </small>
                    )}
                    {blog.images && blog.images.length > 0 && (
                      <div className="mt-3 d-flex flex-wrap gap-2">
                        {blog.images.map((img, idx) => (
                          <div key={idx} className="position-relative">
                            <img
                              src={img}
                              alt={`Gallery ${idx + 1}`}
                              className="rounded shadow"
                              style={{
                                width: "120px",
                                height: "120px",
                                objectFit: "cover",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                              style={{ padding: "2px 6px", fontSize: "12px" }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      {blog.images?.length || 0}/30 rasmlar yuklandi
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Excerpt (English)
                      <small className="text-muted ms-2">
                        (Short summary for blog preview - 150-200 characters)
                      </small>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={200}
                      value={blog.excerpt.en}
                      onChange={(e) =>
                        setBlog({
                          ...blog,
                          excerpt: { ...blog.excerpt, en: e.target.value },
                        })
                      }
                      className="admin-input-premium"
                      placeholder="Brief summary that appears in blog list and search results..."
                    />
                    <Form.Text className="text-muted">
                      {blog.excerpt.en?.length || 0}/200 characters
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Excerpt (Uzbek)
                      <small className="text-muted ms-2">
                        (Blog previewi uchun qisqa tavsif - 150-200 belgi)
                      </small>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={200}
                      value={blog.excerpt.uz}
                      onChange={(e) =>
                        setBlog({
                          ...blog,
                          excerpt: { ...blog.excerpt, uz: e.target.value },
                        })
                      }
                      className="admin-input-premium"
                      placeholder="Blog ro'yxatida ko'rinadigan qisqa tavsif..."
                    />
                    <Form.Text className="text-muted">
                      {blog.excerpt.uz?.length || 0}/200 characters
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Content (English)
                    </Form.Label>
                    <RichTextEditor
                      value={blog.content.en}
                      onChange={(val) =>
                        setBlog({
                          ...blog,
                          content: { ...blog.content, en: val },
                        })
                      }
                      placeholder="Write your content here..."
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Content (Uzbek)
                    </Form.Label>
                    <RichTextEditor
                      value={blog.content.uz}
                      onChange={(val) =>
                        setBlog({
                          ...blog,
                          content: { ...blog.content, uz: val },
                        })
                      }
                      placeholder="Matn yozing..."
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-purple fw-bold">
                      Tags
                    </Form.Label>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Control
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" && (e.preventDefault(), addTag())
                        }
                        placeholder="Add tag"
                        className="admin-input-premium"
                      />
                      <Button onClick={addTag} variant="purple-premium">
                        Add
                      </Button>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {blog.tags?.map((tag, idx) => (
                        <span key={idx} className="badge bg-purple px-3 py-2">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(idx)}
                            className="btn-close btn-close-white ms-2"
                            style={{ fontSize: "10px" }}
                          />
                        </span>
                      ))}
                    </div>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Check
                    type="switch"
                    label="Publish Now"
                    checked={blog.published}
                    onChange={(e) =>
                      setBlog({ ...blog, published: e.target.checked })
                    }
                    className="premium-check fw-bold text-purple"
                  />
                </Col>
              </Row>

              <div className="text-end mt-5">
                <Button
                  variant="outline-light"
                  className="me-3 px-4"
                  onClick={() => navigate("/admin")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="purple-premium"
                  className="px-5 shadow"
                >
                  Save Blog Post
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default BlogForm;
